import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import Hero from '@/components/Hero';
import IntegrationLogoCloud from '@/components/IntegrationLogoCloud';
import ImprovedProductComparison from '@/components/ImprovedProductComparison';
import CallToActionV1 from '@/components/CallToActionV1';
import BenefitsCards from '@/components/BenefitsCards';
import TableChatHeroSection from '@/components/TableChatHeroSection';
import Footer from '@/components/Footer';
import AiPoweredProductAssistant from '@/components/AiPoweredProductAssistant';
import SmarterRevenueDecisions from '@/components/SmarterRevenueDecisions';
import WorkflowJobs from '@/components/WorkflowJobs';
import HowItWorks from '../components/HowItWorks';
import useAnalytics from '../hooks/useAnalytics';
import { useScrollHandler } from '@/utils/scrollUtils';
import LogoScroll from '@/components/LogoScroll';

// Get GTM ID from environment variable
const GTM_ID = import.meta.env.VITE_GTM_ID;

const Index = () => {
  const location = useLocation();
  const analytics = useAnalytics();

  // Use our scroll library
  useScrollHandler(location.hash.slice(1));
  
  // Track page view when component mounts
  useEffect(() => {
    analytics.trackPageView({
      pageCategory: 'home',
      pageSection: 'main',
      contentType: 'landing',
      url: window.location.href,
      title: document.title
    });
  }, []);

  const handleDemoRequest = () => {
    analytics.trackDemoRequest({
      source: 'homepage',
      buttonLocation: 'hero',
      userType: 'prospect'
    });
    // ... existing demo request logic ...
  };

  const handleFeatureClick = (featureName: string) => {
    analytics.trackFeatureInteraction({
      featureName,
      featureCategory: 'product',
      interactionType: 'click',
      source: 'homepage'
    });
    // ... existing feature click logic ...
  };

  // Add scroll depth tracking
  useEffect(() => {
    let lastScrollDepth = 0;
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const scrollPercentage = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);

      // Track at 25%, 50%, 75%, and 100%
      const depths = [25, 50, 75, 100];
      depths.forEach(depth => {
        if (scrollPercentage >= depth && lastScrollDepth < depth) {
          analytics.trackScrollDepth({
            depth: depth,
            pageSection: 'homepage',
            timing: new Date().getTime()
          });
        }
      });
      lastScrollDepth = scrollPercentage;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Enhanced structured data with more detailed information
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Strive",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": "Strive bridges RevOps with Product Operations—ensuring customer needs drive every product decision. Transform your product management with AI-powered insights.",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/OnlineOnly",
      "price": "Contact for Pricing",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "AI-Powered Document Analysis",
      "Real-time Web Content Monitoring",
      "Intelligent Web Crawling",
      "Knowledge Base Management",
      "Custom Workflow Automation",
      "Revenue Intelligence Analytics",
      "Product Analytics Dashboard"
    ],
    "applicationSuite": "Product Operations Platform",
    "keywords": "AI Product Operations, RevOps, Product Management, AI Insights, Business Intelligence, Product Analytics, Knowledge Management",
    "url": window.location.origin,
    "provider": {
      "@type": "Organization",
      "name": "Strive",
      "url": window.location.origin,
      "logo": `${window.location.origin}/og-image.png`
    },
    "audience": {
      "@type": "BusinessAudience",
      "audienceType": "Product Teams, Revenue Teams, Enterprise Organizations"
    },
    "category": "Business Software",
    "downloadUrl": window.location.origin,
    "releaseNotes": "AI-powered platform for streamlining product operations",
    "screenshot": `${window.location.origin}/product-screenshot.png`,
    "softwareRequirements": "Web Browser"
  };

  // Enhanced organization structured data
  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Strive Software, Inc.",
    "url": window.location.origin,
    "logo": `${window.location.origin}/White_Logomark.svg`,
    "sameAs": [
      "https://www.linkedin.com/company/strive-ai/",
      "https://www.youtube.com/@strive-ai/videos",
      "https://matters-of-context.strive.us/"
    ],
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "sales",
        "url": `${window.location.origin}/contact`,
        "availableLanguage": ["English"]
      }
    ],
    "description": "AI-powered product operations platform that transforms customer feedback into actionable insights for revenue and product teams.",
    "foundingDate": "2022",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "600 1st Ave Ste 330 PMB 94465",
      "addressLocality": "Seattle",
      "addressRegion": "WA",
      "postalCode": "98104",
      "addressCountry": "US"
    }
  };

  return (
    <div className="min-h-screen font-sans" lang="en">
      <Helmet>
        {/* Google Tag Manager */}
        <script>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GTM_ID}');`}
        </script>
        
        {/* Primary Meta Tags */}
        <html lang="en" />
        <title>Strive | Product Operations & Revenue Intelligence Platform</title>
        <meta name="title" content="Strive | Product Operations & Revenue Intelligence Platform" />
        <meta name="description" content="Strive transforms product data and customer feedback into actionable insights. Our AI platform helps teams make data-driven product decisions for business growth." />
        <meta name="keywords" content="product data, customer insights, feedback analytics, revenue growth, strive platform, data-driven insights, product management" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="author" content="Strive" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://strive.us" />
        
        {/* Enhanced Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://strive.us" />
        <meta property="og:title" content="Strive | Product Operations & Revenue Intelligence Platform" />
        <meta property="og:description" content="Strive transforms product data and customer feedback into actionable insights. Our AI platform helps teams make data-driven product decisions for business growth." />
        <meta property="og:image" content="/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Strive" />
        <meta property="og:locale" content="en_US" />
        
        {/* Enhanced Twitter Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@StrivePlatform" />
        <meta name="twitter:creator" content="@StrivePlatform" />
        <meta name="twitter:url" content={window.location.href} />
        <meta name="twitter:title" content="Strive | Product Operations & Revenue Intelligence Platform" />
        <meta name="twitter:description" content="Strive helps product managers analyze customer calls, meetings, and emails with AI to extract feedback, feature requests, and churn risks in a single dashboard." />
        <meta name="twitter:image" content="/og-image.png" />
        <meta name="twitter:image:alt" content="Strive Product Intelligence Dashboard" />
        
        {/* Additional SEO Tags */}
        <link rel="alternate" hrefLang="en" href={window.location.href} />
        <meta name="format-detection" content="telephone=no" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="Strive" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

        {/* Enhanced JSON-LD structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Strive Product Intelligence Platform",
            "applicationCategory": "BusinessApplication",
            "description": "Transform product feedback into actionable insights with real-time customer data analysis and revenue intelligence.",
            "keywords": "product intelligence, customer insights, revenue growth, data analytics, product feedback",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "Contact for Pricing"
            }
          })}
        </script>
      </Helmet>

      {/* Google Tag Manager (noscript) */}
      <noscript>
        <iframe 
          src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
          height="0" 
          width="0" 
          style={{ display: 'none', visibility: 'hidden' }}
          title="Google Tag Manager"
        />
      </noscript>
      
      <header className="relative z-50">
        <Navbar />
      </header>

      <main id="main-content">
        {/* Hero Section */}
        <section aria-labelledby="hero-heading" className="relative">
          <Hero />
        </section>

        {/* Social Proof Section */}
        <LogoScroll />

        {/* Product Features Section */}
        <ImprovedProductComparison />

        {/* How It Works Section */}
        <section aria-labelledby="how-it-works-heading" id="how-it-works" className="relative">
          <HowItWorks />
        </section>

        {/* Knowledge Base Section */}
        {/* Removed KnowledgeBaseSection import and usage */}

        {/* Workflow Section */}
        <WorkflowJobs />

        {/* Table Chat Section */}
        <TableChatHeroSection />

        {/* Benefits Section */}
        <BenefitsCards />

        {/* AI Assistant Section */}
        <AiPoweredProductAssistant />

        {/* Revenue Section */}
        <SmarterRevenueDecisions />

        {/* CTA Section */}
        <CallToActionV1 />
      </main>
      
      <footer className="relative">
        <Footer />
      </footer>
    </div>
  );
};

export default Index;
